import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import {
  FileText, Loader2, Download, PenLine, Upload, AlertCircle, CheckCircle2,
  Pause, XCircle, Ban,
} from 'lucide-react';
import cultivarService from '../../services/cultivar';
import LicenseEnforcementModal from '../../components/cultivar/LicenseEnforcementModal';
import { ContractHistory, Fingerprint } from '../../components/cultivar/contracts/ContractBits';
import { formatDateTimeInTz, formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import { ApiError } from '../../types/api';
import type { LicenseTier } from '../../types/cultivar';
import type { NumericValue } from '@/utils/numeric';

interface Agreement {
  id: number;
  license_tier: LicenseTier;
  cultivar_name: string | null;
  cultivar_trade_name?: string | null;
  licensor_org_name: string;
  licensee_org_name: string;
  supplying_nursery_name?: string;
  max_trees?: number;
  tolerance_pct?: NumericValue;
  region?: string;
  start_date?: string;
  end_date?: string;
  merged_terms?: string;
  status: string;
  licensor_confirmed: boolean;
  licensee_confirmed: boolean;
  signed_at?: string;
  signer_name?: string;
  signature_method?: 'e_sig' | 'uploaded';
  signed_pdf_available: boolean;
  suspended_at?: string;
  suspension_reason_public?: string;
  terminated_at?: string;
  termination_reason_public?: string;
  /** SHA-256 of the text below, fixed when it was sent. */
  content_hash?: string | null;
  template_version?: number | null;
  contract_family_id?: number | null;
  contract_name?: string | null;
  /** False when the version this was sent on has since been replaced. Null before versioning. */
  version_in_force?: boolean | null;
  superseded_by_license_id?: number | null;
  void_reason?: string | null;
  is_licensor?: boolean;
}

const TIER_LABELS: Record<LicenseTier, string> = {
  nursery_master_sublicence: 'Master Nursery Sublicence',
  nursery_propagation: 'Nursery Propagation Licence',
  orchard_planting: 'Orchard Planting Licence',
};

export default function LicenseAgreementView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const [agreement, setAgreement] = useState<Agreement | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [signerName, setSignerName] = useState('');
  const [agree, setAgree] = useState(false);
  const [signing, setSigning] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [enforcementAction, setEnforcementAction] = useState<'suspend' | 'terminate' | null>(null);
  const [declining, setDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [historyKey, setHistoryKey] = useState(0);

  const load = useCallback(async (lid: number) => {
    setLoading(true);
    try {
      const res = await cultivarService.getLicenseAgreement(lid);
      if (res.success && res.data) setAgreement(res.data);
      else setLoadError(res.message || 'Unable to load agreement');
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load agreement');
    } finally {
      setLoading(false);
      setHistoryKey(k => k + 1);
    }
  }, []);

  useEffect(() => {
    if (id) load(Number(id));
  }, [id, load]);

  // A refusal because the text moved, or the agreement was replaced, is a
  // reason to reload and read again rather than an error to retry.
  const onSignError = (e: unknown, fallback: string) => {
    if (e instanceof ApiError && (e.code === 'CONTRACT_CHANGED' || e.code === 'CONTRACT_SUPERSEDED') && id) {
      setError(e.message);
      load(Number(id));
      return;
    }
    setError(e instanceof Error ? e.message : fallback);
  };

  const handleSign = async () => {
    if (!id || !agree || !signerName.trim() || !agreement) return;
    setSigning(true);
    setError(null);
    try {
      const res = await cultivarService.signLicenseElectronic(Number(id), signerName.trim(), agreement.content_hash);
      if (res.success) await load(Number(id));
      else setError(res.message || 'Signing failed');
    } catch (e) {
      onSignError(e, 'Signing failed');
    } finally {
      setSigning(false);
    }
  };

  const handleUpload = async () => {
    if (!id || !uploadFile || !agreement) return;
    setSigning(true);
    setError(null);
    try {
      const res = await cultivarService.uploadSignedLicense(Number(id), uploadFile, signerName.trim() || undefined, agreement.content_hash);
      if (res.success) await load(Number(id));
      else setError('Upload failed');
    } catch (e) {
      onSignError(e, 'Upload failed');
    } finally {
      setSigning(false);
    }
  };

  const handleDecline = async () => {
    if (!id) return;
    setError(null);
    try {
      const res = await cultivarService.rejectLicense(Number(id), declineReason.trim() || undefined);
      if (res.success) { setDeclining(false); await load(Number(id)); }
      else setError(res.message || 'Could not decline');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not decline');
    }
  };

  const handleDownload = async () => {
    if (!id) return;
    try {
      const blob = await cultivarService.downloadSignedLicensePdf(Number(id));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `licence-${id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Download failed');
    }
  };

  const handleEnforcementSubmit = async (reasons: { internal: string; public: string }) => {
    if (!id || !enforcementAction) return;
    const fn = enforcementAction === 'suspend'
      ? cultivarService.suspendLicense
      : cultivarService.terminateLicense;
    const res = await fn.call(cultivarService, Number(id), reasons);
    if (!res.success) {
      throw new Error(res.message || `${enforcementAction} failed`);
    }
    setEnforcementAction(null);
    await load(Number(id));
  };

  if (loading && !agreement) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="Licence Agreement" icon={<FileText className="h-6 w-6 text-white" />} />
        <div className="flex justify-center py-20">
          <Loader2 className="h-10 w-10 animate-spin text-primary-600" />
        </div>
      </div>
    );
  }

  if (loadError || !agreement) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="Licence Agreement" icon={<FileText className="h-6 w-6 text-white" />} />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Card><CardContent className="p-8 text-center">
            <AlertCircle className="h-10 w-10 text-red-500 mx-auto mb-3" />
            <p className="text-neutral-700">{loadError || 'Agreement not found'}</p>
            <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">Back</Button>
          </CardContent></Card>
        </div>
      </div>
    );
  }

  const alreadySigned = agreement.licensee_confirmed;
  const isLicensor = !!agreement.is_licensor;
  const isSuspended = agreement.status === 'suspended';
  const isTerminated = agreement.status === 'terminated';
  const isRejected = agreement.status === 'rejected';
  const isSuperseded = agreement.status === 'superseded';
  const isActive = agreement.status === 'active';
  // Out of date: sent on a version since replaced, and not yet replaced itself
  // (an agreement issued before versioning that nobody reissued).
  const outOfDate = !alreadySigned && !isSuperseded && agreement.version_in_force === false;
  const canSign = !isLicensor && !alreadySigned && !isTerminated && !isRejected && !isSuperseded && !outOfDate;
  const tierLabel = TIER_LABELS[agreement.license_tier] || 'Licence';

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={tierLabel}
        subtitle={agreement.cultivar_trade_name || agreement.cultivar_name || agreement.contract_name || undefined}
        icon={<FileText className="h-6 w-6 text-white" />}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

        {isSuperseded && (
          <Card className="border-neutral-300 bg-neutral-100">
            <CardContent className="p-5 flex items-start gap-3">
              <Ban className="h-6 w-6 text-neutral-500 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="font-semibold text-neutral-900">This agreement was withdrawn and can no longer be signed.</p>
                {agreement.void_reason && <p className="text-sm text-neutral-700 mt-1 break-words">{agreement.void_reason}</p>}
                {agreement.superseded_by_license_id && (
                  <Link to={`/cultivar/licences/${agreement.superseded_by_license_id}/agreement`} className="inline-block mt-2 text-sm text-primary-600 hover:underline">
                    Open the agreement that replaces it (#{agreement.superseded_by_license_id})
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {outOfDate && (
          <Card className="border-amber-300 bg-amber-50">
            <CardContent className="p-5 text-sm text-amber-900">
              This agreement was sent on version {agreement.template_version ?? 'an earlier version'} of
              {' '}{agreement.contract_name ? `“${agreement.contract_name}”` : 'the contract'}, which has since been
              replaced. It cannot be signed. {isLicensor ? 'Issue it again to send the current version.' : `Ask ${agreement.licensor_org_name} to send the current version.`}
            </CardContent>
          </Card>
        )}

        {/* Status banner — suspended / terminated */}
        {(isSuspended || isTerminated) && (
          <Card className={isTerminated ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}>
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                {isTerminated
                  ? <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
                  : <Pause className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                }
                <div>
                  <p className={`font-semibold ${isTerminated ? 'text-red-900' : 'text-amber-900'}`}>
                    Licence {isTerminated ? 'terminated' : 'suspended'} by {agreement.licensor_org_name}
                  </p>
                  <p className="text-xs uppercase tracking-wide text-neutral-600 mt-2 mb-1">Reason</p>
                  <p className={`text-sm ${isTerminated ? 'text-red-900' : 'text-amber-900'}`}>
                    {isTerminated
                      ? agreement.termination_reason_public
                      : agreement.suspension_reason_public}
                  </p>
                  <p className="text-xs text-neutral-600 mt-3">
                    {isTerminated
                      ? 'A terminated licence is permanent and cannot be reinstated. Contact the licensor directly if you believe this is in error.'
                      : 'A suspended licence is paused — no propagation, sale, or planting may occur until reinstated. Contact the licensor for clarification.'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {isRejected && (
          <Card className="border-neutral-300 bg-neutral-50">
            <CardContent className="p-5 text-sm text-neutral-700">This agreement was declined by {agreement.licensee_org_name}.</CardContent>
          </Card>
        )}

        <Card>
          <CardHeader><CardTitle>Parties</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div><span className="text-neutral-500">Licensor:</span> <span className="font-medium">{agreement.licensor_org_name}</span></div>
            <div><span className="text-neutral-500">Licensee:</span> <span className="font-medium">{agreement.licensee_org_name}</span></div>
            {agreement.supplying_nursery_name && (
              <div><span className="text-neutral-500">Supplying nursery:</span> <span className="font-medium">{agreement.supplying_nursery_name}</span></div>
            )}
            {agreement.region && <div><span className="text-neutral-500">Region:</span> {agreement.region}</div>}
            {agreement.max_trees != null && (
              <div><span className="text-neutral-500">Max trees:</span> {agreement.max_trees} (±{agreement.tolerance_pct}%)</div>
            )}
            {agreement.end_date && (
              <div><span className="text-neutral-500">End date:</span> {formatLocalDateInTz(String(agreement.end_date).slice(0, 10), orgTz)}</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Terms</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="text-sm text-neutral-600 space-y-1">
              {agreement.contract_name ? (
                <p>{agreement.contract_name}{agreement.template_version ? `, version ${agreement.template_version}` : ' — version not recorded (sent before contract versioning)'}</p>
              ) : null}
              <Fingerprint hash={agreement.content_hash} label="Fingerprint of this text" />
              <p className="text-xs text-neutral-500">
                The same fingerprint is printed on the PDF. It changes if even one character of the text changes,
                so a copy with a matching fingerprint is this exact agreement.
              </p>
            </div>
            {/* Shown as text, exactly as stored and fingerprinted — never
                interpreted as HTML, which could show something other than
                what is being signed. */}
            <div className="text-sm text-neutral-800 whitespace-pre-wrap break-words bg-neutral-50 border border-neutral-200 rounded p-3">
              {agreement.merged_terms || 'No terms text.'}
            </div>
          </CardContent>
        </Card>

        {alreadySigned ? (
          <Card>
            <CardHeader><CardTitle>Signature</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">Signed by {agreement.signer_name}</span>
              </div>
              <div className="text-sm text-neutral-600">
                Method: {agreement.signature_method === 'uploaded' ? 'Uploaded PDF' : 'Electronic signature'}<br />
                Signed at: {agreement.signed_at ? formatDateTimeInTz(agreement.signed_at, orgTz) : '—'}
              </div>
              {agreement.signed_pdf_available && (
                <Button variant="outline" onClick={handleDownload}>
                  <Download className="h-4 w-4 mr-2" /> Download signed PDF
                </Button>
              )}
            </CardContent>
          </Card>
        ) : isTerminated ? (
          <Card>
            <CardContent className="p-6 text-center text-neutral-600 text-sm">
              This licence was terminated before it could be signed.
            </CardContent>
          </Card>
        ) : canSign ? (
          <Card>
            <CardHeader><CardTitle>Sign this licence</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2"><PenLine className="h-4 w-4" /> Option A — Electronic signature</h4>
                <label htmlFor="signer-name" className="sr-only">Your full legal name</label>
                <input
                  id="signer-name"
                  type="text"
                  placeholder="Your full legal name"
                  value={signerName}
                  onChange={e => setSignerName(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm"
                />
                <label className="flex items-start gap-2 text-sm">
                  <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} className="mt-1" />
                  <span>
                    I have read the terms above (fingerprint {agreement.content_hash ? agreement.content_hash.slice(0, 8) : '—'}) and
                    agree to be bound on behalf of <strong>{agreement.licensee_org_name}</strong>.
                  </span>
                </label>
                <Button
                  variant="primary"
                  onClick={handleSign}
                  disabled={!agree || !signerName.trim() || signing}
                >
                  {signing ? 'Signing…' : 'Sign electronically'}
                </Button>
              </div>

              <div className="border-t pt-6 space-y-3">
                <h4 className="font-semibold flex items-center gap-2"><Upload className="h-4 w-4" /> Option B — Upload countersigned PDF</h4>
                <p className="text-xs text-neutral-500">
                  Print the unsigned PDF that was emailed when the licence was issued, sign it,
                  scan it (or take a clear photo) and upload it here. Check the fingerprint printed
                  on it matches the one above.
                </p>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={e => setUploadFile(e.target.files?.[0] || null)}
                  className="text-sm max-w-full"
                />
                <Button variant="outline" onClick={handleUpload} disabled={!uploadFile || signing}>
                  {signing ? 'Uploading…' : 'Upload signed PDF'}
                </Button>
              </div>

              <div className="border-t pt-6 space-y-3">
                {!declining ? (
                  <Button variant="ghost" onClick={() => setDeclining(true)}>Decline this agreement</Button>
                ) : (
                  <div className="space-y-2">
                    <label htmlFor="decline-reason" className="block text-sm font-semibold text-neutral-700">Why are you declining? (optional, sent to {agreement.licensor_org_name})</label>
                    <textarea
                      id="decline-reason"
                      rows={2}
                      value={declineReason}
                      onChange={e => setDeclineReason(e.target.value)}
                      className="w-full border rounded px-3 py-2 text-sm"
                    />
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" onClick={() => setDeclining(false)}>Cancel</Button>
                      <Button variant="danger" onClick={handleDecline}>Decline</Button>
                    </div>
                  </div>
                )}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">{error}</div>
              )}
            </CardContent>
          </Card>
        ) : isLicensor && !isSuperseded && !isRejected && !outOfDate ? (
          <Card>
            <CardContent className="p-6 text-sm text-neutral-600">
              Waiting for {agreement.licensee_org_name} to sign.
            </CardContent>
          </Card>
        ) : null}

        {!canSign && error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">{error}</div>
        )}

        {/* Licensor enforcement actions */}
        {isLicensor && (isActive || isSuspended) && (
          <Card>
            <CardHeader><CardTitle>Enforcement actions</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-neutral-600">
                Suspending or terminating a licence emails the licensee and every cultivar admin
                in your organisation. The reason given to the licensee is brief and shown on
                screen and in the email; the internal reason is recorded in the audit log only.
              </p>
              <div className="flex flex-wrap gap-3">
                {isActive && (
                  <Button variant="outline" onClick={() => setEnforcementAction('suspend')}>
                    <Pause className="h-4 w-4 mr-2" />
                    Suspend licence
                  </Button>
                )}
                <Button variant="danger" onClick={() => setEnforcementAction('terminate')}>
                  <XCircle className="h-4 w-4 mr-2" />
                  Terminate licence
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-4">
            <ContractHistory kind="license" id={agreement.id} title="What has happened to this agreement" refreshKey={historyKey} />
          </CardContent>
        </Card>

        {enforcementAction && (
          <LicenseEnforcementModal
            action={enforcementAction}
            onSubmit={handleEnforcementSubmit}
            onClose={() => setEnforcementAction(null)}
          />
        )}
      </div>
    </div>
  );
}
